<template>
  <section class="auth-page">
    <b-container class="auth-container">
      <b-card class="auth-card">
        <h1 class="auth-title">Create your account</h1>
        <p class="auth-subtitle">
          Save essays, get personalized feedback history, and pick up where you left off.
        </p>

        <b-alert v-if="errorMessage" show variant="danger" class="mb-3">
          {{ errorMessage }}
        </b-alert>

        <b-form @submit.prevent="handleSignup">
          <b-form-group label="Name (optional)" label-for="signup-name">
            <b-form-input
              id="signup-name"
              v-model.trim="displayName"
              autocomplete="name"
              placeholder="Your name"
            />
          </b-form-group>

          <b-form-group label="Email" label-for="signup-email">
            <b-form-input
              id="signup-email"
              v-model.trim="email"
              type="email"
              required
              autocomplete="email"
              placeholder="you@example.com"
            />
          </b-form-group>

          <b-form-group label="Password" label-for="signup-password">
            <b-input-group>
              <b-form-input
                id="signup-password"
                v-model="password"
                :type="passwordVisible ? 'text' : 'password'"
                required
                minlength="6"
                autocomplete="new-password"
                placeholder="At least 6 characters"
              />
              <b-input-group-append>
                <b-button
                  variant="outline-secondary"
                  type="button"
                  @click="passwordVisible = !passwordVisible"
                >
                  {{ passwordVisible ? "Hide" : "Show" }}
                </b-button>
              </b-input-group-append>
            </b-input-group>
          </b-form-group>

          <b-form-group label="Confirm password" label-for="signup-confirm-password">
            <b-input-group>
              <b-form-input
                id="signup-confirm-password"
                v-model="confirmPassword"
                :type="confirmPasswordVisible ? 'text' : 'password'"
                required
                autocomplete="new-password"
                placeholder="Retype your password"
              />
              <b-input-group-append>
                <b-button
                  variant="outline-secondary"
                  type="button"
                  @click="confirmPasswordVisible = !confirmPasswordVisible"
                >
                  {{ confirmPasswordVisible ? "Hide" : "Show" }}
                </b-button>
              </b-input-group-append>
            </b-input-group>
          </b-form-group>

          <b-button type="submit" variant="primary" class="w-100" :disabled="submitting">
            {{ submitting ? "Creating account..." : "Sign up" }}
          </b-button>
        </b-form>

        <div class="auth-links mt-3">
          Already have an account?
          <router-link :to="loginLink">Log in</router-link>
        </div>
      </b-card>
    </b-container>
  </section>
</template>

<script>
import { computed, onMounted, ref } from "vue";
import { useRoute, useRouter } from "vue-router";
import { signUpUser } from "@/services/authService";
import { trackEvent } from "@/services/analyticsService";

export default {
  name: "SignupView",
  setup() {
    const route = useRoute();
    const router = useRouter();

    const displayName = ref("");
    const email = ref("");
    const password = ref("");
    const confirmPassword = ref("");
    const passwordVisible = ref(false);
    const confirmPasswordVisible = ref(false);
    const submitting = ref(false);
    const errorMessage = ref("");

    const redirectTarget = computed(() => {
      return typeof route.query.redirect === "string"
        ? route.query.redirect
        : "/account";
    });

    const loginLink = computed(() => ({
      name: "Login",
      query: route.query.redirect ? { redirect: route.query.redirect } : {}
    }));

    const clearBodyScrollLock = () => {
      if (typeof document === "undefined") {
        return;
      }

      document.body.classList.remove("modal-open");
      document.body.style.removeProperty("overflow");
      document.body.style.removeProperty("padding-right");
    };

    onMounted(() => {
      clearBodyScrollLock();
    });

    const handleSignup = async () => {
      trackEvent("signup_started", {
        entry_path: route.fullPath
      });

      errorMessage.value = "";

      if (password.value.length < 6) {
        errorMessage.value = "Please choose a password with at least 6 characters.";
        return;
      }

      if (password.value !== confirmPassword.value) {
        errorMessage.value = "Passwords do not match.";
        return;
      }

      submitting.value = true;
      try {
        await signUpUser(email.value, password.value, {
          displayName: displayName.value
        });
        trackEvent("signup_completed", {
          redirect_target: redirectTarget.value
        });
        await router.push(redirectTarget.value);
      } catch (error) {
        errorMessage.value = error.message || "Unable to create your account right now.";
      } finally {
        submitting.value = false;
      }
    };

    return {
      displayName,
      email,
      password,
      confirmPassword,
      passwordVisible,
      confirmPasswordVisible,
      submitting,
      errorMessage,
      loginLink,
      handleSignup
    };
  }
};
</script>

<style scoped>
.auth-page {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 24px 16px;
  min-height: 100vh;
  width: 100%;
}

.auth-container {
  max-width: 560px;
}

.auth-card {
  border-radius: 14px;
  border: 1px solid #ececec;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.08);
  height: auto;
}

.auth-title {
  font-size: 30px;
  margin-bottom: 8px;
}

.auth-subtitle {
  color: #5b616e;
  margin-bottom: 16px;
}

.auth-links {
  font-size: 14px;
}
</style>
